// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/operacional/hooks/useCrm.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { crmService, type ListaAtendimentosFiltros } from '../services/crmService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type {
  CriarAtendimentoRequest,
  AtualizarAtendimentoRequest,
  AdicionarContatoRequest,
} from '../types';

interface UseCrmParams {
  pagina?: number;
  tamanhoPagina?: number;
  filtros?: ListaAtendimentosFiltros;
}

export const useCrm = ({ pagina = 1, tamanhoPagina = 20, filtros = {} }: UseCrmParams = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: ['crm', 'lista', pagina, tamanhoPagina, filtros],
    queryFn: () => crmService.getAll(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const atendimentos  = paginado?.itens       ?? [];
  const total         = paginado?.total        ?? 0;
  const totalPaginas  = paginado?.totalPaginas ?? 1;
  const contadores = {
    pendentes:  paginado?.totalPendentes  ?? 0,
    concluidos: paginado?.totalConcluidos ?? 0,
    urgentes:   paginado?.totalUrgentes   ?? 0,
  };

  const createMutation = useMutation({
    mutationFn: (request: CriarAtendimentoRequest) => crmService.create(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Atendimento criado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarAtendimentoRequest }) =>
      crmService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Atendimento atualizado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => crmService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Atendimento excluído com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const adicionarContatoMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AdicionarContatoRequest }) =>
      crmService.adicionarContato(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Contato registrado com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const removerContatoMutation = useMutation({
    mutationFn: ({ atendimentoId, contatoId }: { atendimentoId: number; contatoId: number }) =>
      crmService.removerContato(atendimentoId, contatoId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm'] });
      toast.success('Contato removido com sucesso');
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    atendimentos,
    total,
    totalPaginas,
    contadores,
    isLoading,
    isFetching,
    createAtendimento: createMutation.mutate,
    updateAtendimento: updateMutation.mutate,
    deleteAtendimento: deleteMutation.mutate,
    adicionarContato: adicionarContatoMutation.mutate,
    removerContato: removerContatoMutation.mutate,
    isCreating: createMutation.isPending,
  };
};
